import func from "@uikit/func";
import { useSelector } from "react-redux";
import { useRef, useImperativeHandle, useEffect, forwardRef, useState } from "react";
import ImageLoading from "../../../../views/components/ImageLoading/ImageLoading";
import style from "./ImageNode.module.css";
import { get, isEmpty } from "lodash";
import { useTimeoutQuery } from "./useTimeoutQuery";
import { Button, Modal } from "antd";
import { BaseUploadNodeProps } from "imagica-uikit/dist/nodesV2/typing";

export type ImageProps = {
  isInBlueprint?: boolean;
  id: string;
  isResized: boolean;
  isLongPress: boolean;
  checkNodeArr: any[];
  handleGroupStyle: Function;
  parentNodeId: string;
  textAreaValue: {
    loading: boolean;
    value: string;
    messageId?: string;
    meId?: string;
    progress?: string;
    polling?: boolean;
    resolve?: (url: string) => void;
  };
  isTargetNode: boolean;
  showFeedback: boolean;
  enableFeedback?: boolean;
  displayType: string;
  createBlobUrl?: BaseUploadNodeProps["createBlobUrl"];
};

type HandleImageReportSize = {
  width: number;
  height: number;
};

type Handle = {
  initialImageReportSize: HandleImageReportSize;
};

export const Image = forwardRef<Handle, ImageProps>((props, customImageNodeRef) => {
  const messageId = get(props, "textAreaValue.messageId", "");
  const meId = get(props, "textAreaValue.meId", "");
  const { isTimeout, queryUrl, loading: queryLoading, progress } = useTimeoutQuery(messageId, props.id, meId);

  const compatibleValue = props?.textAreaValue?.value || props.createBlobUrl?.objectURL || props?.textAreaValue;
  const describeRef = useRef<null | HTMLDivElement>(null);

  const { loading, polling, progress: corekitProgress } = props?.textAreaValue || {};

  const imagicaStudioTutorial = useSelector((state: any) => state.fot.imagicaStudioTutorial);

  useEffect(() => {
    if (!imagicaStudioTutorial.showGuide) {
      setTimeout(() => {
        props?.handleGroupStyle?.();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagicaStudioTutorial]);

  useImperativeHandle(customImageNodeRef, () => ({
    initialImageReportSize: getInitialImageReportSize(),
  }));

  const getInitialImageReportSize: () => HandleImageReportSize = () => {
    return {
      width: 295,
      height: 355 + ((imagicaStudioTutorial.showGuide && describeRef?.current?.offsetHeight) || 0),
    };
  };

  const getImageMinHeight = () => {
    if (func.isEmpty(describeRef?.current?.offsetHeight) || !imagicaStudioTutorial.showGuide) return {};
    return {
      height: `calc(100% - ${(describeRef.current?.offsetHeight as number) + 60}px)`,
    };
  };
  const openPreviewImg = () => {
    setShowModal(true);
  };
  const handleModalCancel = () => {
    setShowModal(false);
  };
  const openNewTab = () => {
    window.open(`${compatibleValue}`);
  };
  const [showModal, setShowModal] = useState(false);

  const showProgress = Math.max(Number(corekitProgress), Number(progress));
  const isInBlueprint = props.isInBlueprint ?? false;

  return (
    <div
      className={`${style["white-frame"]} ${props.enableFeedback && !isInBlueprint ? style["target"] : ""} ${
        props.isLongPress || props.checkNodeArr?.includes(props.id) ? "nowheel nopan" : ""
      }`}
    >
      <div className={style["image-box"]} style={{ marginTop: props.displayType === "uploadImage" ? "8px" : "" }}>
        <div
          onClick={() => typeof compatibleValue === "string" && openPreviewImg()}
          className={`${style["image-content"]} ${
            props.isResized || !isEmpty(props.parentNodeId) || props.displayType === "uploadImage"
              ? ""
              : style["default-resize"]
          }`}
          style={getImageMinHeight()}
        >
          {typeof compatibleValue === "string" && (
            <img
              alt=""
              src={compatibleValue}
              onLoad={() => props?.handleGroupStyle?.()}
              onError={() => props?.handleGroupStyle?.()}
            />
          )}
          <ImageLoading displayType={props.displayType} loading={loading} style={{}} />
          {(isTimeout || polling) && (typeof compatibleValue === "string") === false && (
            <div className={style.timeoutQuery}>
              <Button
                loading={queryLoading}
                onClick={() => {
                  queryUrl();
                }}
              >
                ({showProgress}%) Refresh
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* image preview */}
      <Modal
        title=""
        open={showModal}
        onCancel={handleModalCancel}
        wrapClassName={style["img-preview-modal"]}
        footer={[]}
      >
        {typeof compatibleValue === "string" && (
          <img
            onClick={() => typeof compatibleValue === "string" && openNewTab()}
            className={style.previewImg}
            alt=""
            src={compatibleValue}
            onLoad={() => props?.handleGroupStyle?.()}
            onError={() => props?.handleGroupStyle?.()}
          />
        )}
      </Modal>
    </div>
  );
});
